import React, { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { auth } from 'services/firebase'
import { FirebaseError } from '@firebase/util'
import { Box, Button, Container, Stack, Text, useToast } from '@chakra-ui/react'
import { ProviderSection, TextInput } from 'components'
import styled from '@emotion/styled'

import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { emailRegex } from 'util/helpers'
import { Helmet } from 'react-helmet'

type FormData = {
  email: string
  password: string
  confirmPassword: string
}

const schema = yup
  .object({
    email: yup
      .string()
      .required('Please enter an email')
      .email('Invalid email')
      .matches(emailRegex, 'Invalid email format'),
    password: yup
      .string()
      .required('Please enter a valid password')
      .min(8, 'Your password is too short.')
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character',
      ),
    confirmPassword: yup
      .string()
      .required('You must confirm your password')
      .oneOf([yup.ref('password')], 'Your passwords do not match.'),
  })
  .required()

export const Signup = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  })
  const toast = useToast()

  const onSignup = async (data: FormData) => {
    const { email, password } = data
    setLoading(true)
    try {
      await createUserWithEmailAndPassword(auth, email, password)
      toast({
        title: 'Signed up successfully',
        status: 'success',
        isClosable: true,
      })
      navigate('/login')
    } catch (error) {
      if (error instanceof FirebaseError) {
        toast({
          title: 'Error while signing up',
          description: error.message,
          status: 'error',
          isClosable: true,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box w='100%' p={4}>
      <Helmet>
        <title>Sign Up | Songvisor</title>
      </Helmet>
      <Container maxW='xl'>
        <form onSubmit={handleSubmit(onSignup)}>
          <Stack spacing={4}>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Email'
                  type='email'
                  placeholder='email@example.com'
                  isDisabled={loading}
                  isInvalid={Boolean(errors.email)}
                  helperText={errors.email?.message ? errors.email.message : ''}
                  isRequired
                  {...field}
                />
              )}
            />

            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Password'
                  type='password'
                  placeholder='*******'
                  isDisabled={loading}
                  isInvalid={Boolean(errors.password)}
                  helperText={
                    errors.password?.message
                      ? errors.password.message
                      : 'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character'
                  }
                  isRequired
                  {...field}
                />
              )}
            />

            <Controller
              name='confirmPassword'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Confirm Password'
                  type='password'
                  placeholder='*******'
                  isDisabled={loading}
                  isInvalid={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword?.message ? errors.confirmPassword.message : ''}
                  isRequired
                  {...field}
                />
              )}
            />
          </Stack>
          <ButtonWrapper>
            <Button
              type='submit'
              colorScheme='gray'
              isLoading={loading}
              loadingText='Signing up'
              width='100%'
            >
              Sign up
            </Button>
          </ButtonWrapper>
        </form>

        <ProviderSection pageType='signup' />

        <StyledText>
          Already have an account? <NavLink to='/login'>Login</NavLink>
        </StyledText>
      </Container>
    </Box>
  )
}

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 2rem;
`

const StyledText = styled(Text)`
  margin-top: 1rem;

  a {
    font-weight: bold;
    &:hover {
      opacity: 60%;
    }
  }
`
